/* eslint-disable no-plusplus */
/* eslint-disable no-param-reassign */
import React, { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { lighten, desaturate, darken } from 'polished';
import ReactDOM from 'react-dom';
import { Modifier, usePopper } from 'react-popper';
import { SAIcons, SAInputContainer, SAUX360Theme, SAUXTheme } from '@saux/design-system-react';
import { v4 as uuidv4 } from 'uuid';

interface SuggestionsProps {
  theme: SAUXTheme;
  color?: string;
  listHeight?: number;
}

const Highlighter = ({ children = null, highlight, className }: any) => {
  if (!highlight) return children;
  const regexp = new RegExp(highlight, 'gi');
  if (children) {
    const matches = children?.match(regexp);
    const parts = children?.split(new RegExp(`${highlight.replace()}`, 'gi'));
    // TODO: refactor this, not a fan of the mutation of part
    for (let i = 0; i < parts.length; i++) {
      if (i !== parts.length - 1) {
        let match = matches[i];
        // While the next part is an empty string, merge the corresponding match with the current
        // match into a single <span/> to avoid consequent spans with nothing between them.
        while (parts[i + 1] === '') {
          match += matches[++i] || '';
        }

        parts[i] = (
          <React.Fragment key={i}>
            {parts[i]}
            <span className="highlighted">{match}</span>
          </React.Fragment>
        );
      }
    }
    return <span className={`highlighter ${className}`}>{parts}</span>;
  }
  return <></>
};

const Wrapper = styled('div')<SuggestionsProps>`
  ${({ theme, color }) => {
    const baseColor = color || theme.color.primary.background;
    return css`
      position: relative;
      &:hover {
        .icon-button {
          display: block;
        }
      }
      .icon-button {
        display: none;
        line {
          stroke: ${baseColor};
        }
      }
    `;
  }}
`;

const Box = styled.ul<SuggestionsProps>`
  ${({ theme, color, listHeight }) => {
    const baseColor = color || theme.color.primary.background;
    return css`
      background: #fff;
      list-style: none;
      height: auto;
      max-height: 50vh;
      border: 1px solid ${baseColor};
      border-radius: ${theme.size.radius.small};
      overflow: auto;
      overflow: overlay;
      scroll-behavior: smooth;
      transition: transform 0.15s;
      box-shadow: ${theme.shadow.elevation.level1};
      z-index: 10;
      &::-webkit-scrollbar {
        width: 20px;
        background-color: rgba(255, 255, 255, 0);
      }

      ::-webkit-scrollbar-track,
      ::-webkit-scrollbar-thumb {
        border: 5px solid rgba(255, 255, 255, 0);
        background-clip: padding-box;
      }

      &::-webkit-scrollbar-track {
        background: transparent;
        border-radius: 0 2px 2px 0;
        &:hover {
          background: ${desaturate(0.7, lighten(0.3, baseColor))};
        }
      }

      &::-webkit-scrollbar-thumb {
        background-color: ${baseColor};
        border-radius: 500px;
        margin: 0 2px;
      }
      [data-popper-placement^='top'] & {
        margin-bottom: 10px;
      }
      [data-popper-placement^='bottom'] & {
        margin-top: 5px;
      }
      li {
        height: ${listHeight ? listHeight : 50}px;
        padding: ${theme.size.spacer.medium};
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        color: ${darken(0.15, baseColor)};
        &.selected {
          background: ${desaturate(0.5, lighten(0.4, baseColor))};
        }
        &:hover {
          background: ${desaturate(0.5, lighten(0.5, baseColor))};
          cursor: pointer;
        }
        .highlighted {
          font-weight: bold;
        }
        .line2 {
          font-size: ${theme.font.primary.size.small};
          color: ${darken(0.15, baseColor)};
        }
      }
    `;
  }}
`;

export function AutoCompleteTexting({
  placeholder,
  hint,
  options,
  id,
  label,
  color,
  className,
  listHeight,
  onSelect,
  onClear,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  error,
  theme,
  value,
  fullWidth,
  variant,
  ...rest
}: any) {
  const [activeSuggestion, setActiveSuggestion] = useState(0);
  const [userInput, setUserInput] = useState(value);
  const [dropdownVisible, setDropdownVisible] = useState(true);

  // refs

  const selectedRef = React.useRef<HTMLLIElement | null>(null);

  // Popper

  const [referenceElement, setReferenceElement] = useState<HTMLInputElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [popoverReferenceElement, setPopoverReferenceElement] = useState<Element | null>(null);

  const modifiers = useMemo(
    () => [
      {
        name: 'sameWidth',
        enabled: true,
        fn: ({ state }) => {
          state.styles.popper.width = `${state.rects.reference.width}px`;
          // state.styles.popper.transform = `translate(${state.rects.reference.x}px,230px)`;
        },
        phase: 'beforeWrite',
        requires: ['computeStyles'],
      } as Modifier<'sameWidth', { customOption: any }>,
    ],
    [],
  );

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
    modifiers,
  });

  const handleUserInput = (inputValue: string) => {
    setUserInput(inputValue);
    // setSuggestions(filterSuggestions(inputValue));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUserInput(e.currentTarget.value);
    if (onChange) {
      onChange(e);
    }
  };

  const handleSelection = (index: number) => {
    if (onSelect) {
      onSelect(index)
    }
    setUserInput(options[index]);
    setDropdownVisible(false);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setDropdownVisible(true);
    if (onFocus !== undefined) {
      onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (onBlur !== undefined) {
      onBlur(e);
    }
    setDropdownVisible(false);
  };

  const clearInput = () => {
    setUserInput('')
    if (onClear) {
      onClear()
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    setDropdownVisible(true);
    if (onKeyDown !== undefined) {
      onKeyDown(e);
    }
    if (['Up', 'ArrowUp'].includes(e.key)) {
      if (activeSuggestion === 0) {
        setActiveSuggestion(options.length - 1);
        return;
      }

      setActiveSuggestion(activeSuggestion - 1);
    }
    // User pressed the down arrow
    else if (['Down', 'ArrowDown'].includes(e.key)) {
      if (activeSuggestion === options.length - 1) {
        setActiveSuggestion(0);
        return;
      }

      setActiveSuggestion(activeSuggestion + 1);
    } else if (e.key === 'Enter') {
      handleSelection(activeSuggestion);
    } else {
      setActiveSuggestion(0);
    }
  };

  useEffect(() => {
    if (selectedRef.current && popperElement) {
      popperElement.children[0].scrollTop = selectedRef.current.offsetTop;
    }
  }, [activeSuggestion]);

  function handleClick(event: React.MouseEvent<HTMLElement>) {
    // setPopoverReferenceElement(event.currentTarget);
  }

  function handleClose(event: React.MouseEvent<HTMLElement> | MouseEvent, index: number | null) {
    setPopoverReferenceElement(null);
  }

  return (
    <Wrapper color={color} theme={theme}>
      <SAInputContainer
        variant={variant}
        id={id}
        label={label}
        hint={hint}
        color={color}
        error={error}
        theme={theme}
        className={className}
        fullWidth={true}
        backgroundColor="white"
      >
        <input
          type="search"
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={(e) => handleFocus(e)}
          onBlur={(e) => handleBlur(e)}
          value={userInput}
          placeholder={placeholder}
          aria-activedescendant={dropdownVisible ? `${id}-option-${activeSuggestion}` : undefined}
          aria-autocomplete="list"
          role="combobox"
          aria-controls={`${id}-list`}
          aria-expanded={dropdownVisible && options.length > 0}
          ref={setReferenceElement}
          autoComplete="off"
          {...rest}
        />
      </SAInputContainer>
      {dropdownVisible &&
        options.length > 0 &&
        ReactDOM.createPortal(
          <div
            ref={setPopperElement}
            style={styles.popper}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...attributes.popper}
          >
            <Box role="listbox" color={color} id={`${id}-list`} theme={theme}>
              {options.map((item: any, index: number) => {
                const selected = index === activeSuggestion;
                return (
                  <li
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleSelection(index)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSelection(index)}
                    aria-selected={selected}
                    role="option"
                    key={uuidv4()}
                    className={selected ? 'selected' : ''}
                    id={`${id}-option-${index}`}
                    ref={selected ? selectedRef : undefined}
                    tabIndex={-1}
                    style={{ height: listHeight || 50 }}
                  >
                    <Highlighter highlight={userInput} className="line1">
                      {Array.isArray(item) ? item[0] : item}
                    </Highlighter>
                    {Array.isArray(item) && <span className="line2">{item[1]}</span>}
                  </li>
                );
              })}
            </Box>
          </div>,
          document.body,
        )}
    </Wrapper>
  );
}
